import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import Link from "next/link";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import WinnerDropBadge from "../winner/WinnerDropBadge";
import WaveDropTime from "../time/WaveDropTime";
import ApprovalStatusBadge from "@/components/waves/approval/ApprovalStatusBadge";
import { isOfficiallyApprovedDrop } from "@/helpers/waves/approve-wave.helpers";
import type { DropTimestampLayout } from "../drop.types";
interface ParticipationDropHeaderProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly winningThreshold?: number | null | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
}
export default function ParticipationDropHeader(t0) {
  const $ = _c(35);
  const {
    drop,
    showWaveInfo,
    winningThreshold,
    timestampLayout: t1
  } = t0;
  const timestampLayout = t1 === undefined ? "inline" : t1;
  const isStackedTimestamp = timestampLayout === "stacked";
  const authorIdentity = drop.author.handle ?? drop.author.primary_address;
  const isApproveDrop = typeof winningThreshold === "number" && winningThreshold > 0;
  const isApprovedDrop = isApproveDrop && isOfficiallyApprovedDrop(drop);
  const rank = drop.rank;
  const hasWinnerRank = typeof rank === "number" && rank !== 0 && !Number.isNaN(rank);
  let statusBadge = null;
  if (isApprovedDrop) {
    const t2 = drop.winning_context?.decision_time ?? null;
    const t3 = drop.winning_context?.place ?? rank;
    let t4;
    if ($[0] !== t2 || $[1] !== t3) {
      t4 = <ApprovalStatusBadge approvedAt={t2} order={t3} />;
      $[0] = t2;
      $[1] = t3;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    statusBadge = t4;
  } else {
    if (!isApproveDrop && hasWinnerRank) {
      const t2 = drop.winning_context?.decision_time ?? null;
      let t3;
      if ($[3] !== rank || $[4] !== t2) {
        t3 = <WinnerDropBadge rank={rank} decisionTime={t2} />;
        $[3] = rank;
        $[4] = t2;
        $[5] = t3;
      } else {
        t3 = $[5];
      }
      statusBadge = t3;
    }
  }
  const t2 = isStackedTimestamp ? "tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-gap-y-1" : "tw-flex tw-flex-wrap tw-items-center tw-gap-x-2";
  const t3 = `/${authorIdentity}`;
  let t4;
  if ($[6] !== authorIdentity || $[7] !== t3) {
    t4 = <p className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none"><Link onClick={_temp} href={t3} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-500">{authorIdentity}</Link></p>;
    $[6] = authorIdentity;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== drop.author.level) {
    t5 = <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[9] = drop.author.level;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== isStackedTimestamp) {
    t6 = !isStackedTimestamp && <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" />;
    $[11] = isStackedTimestamp;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== drop.created_at || $[14] !== isStackedTimestamp) {
    t7 = !isStackedTimestamp && <WaveDropTime timestamp={drop.created_at} />;
    $[13] = drop.created_at;
    $[14] = isStackedTimestamp;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== statusBadge || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t8 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{t4}{t5}{statusBadge}{t6}{t7}</div>;
    $[16] = statusBadge;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== drop.created_at || $[23] !== isStackedTimestamp) {
    t9 = isStackedTimestamp && <WaveDropTime timestamp={drop.created_at} />;
    $[22] = drop.created_at;
    $[23] = isStackedTimestamp;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t2 || $[26] !== t8 || $[27] !== t9) {
    t10 = <div className={t2}>{t8}{t9}</div>;
    $[25] = t2;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== drop.wave || $[30] !== showWaveInfo) {
    t11 = showWaveInfo && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      }).chat;
      const isDirectMessage = waveMeta?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link onClick={_temp2} href={waveHref} className="tw-leading-0 tw-mb-0 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[29] = drop.wave;
    $[30] = showWaveInfo;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp(e) {
  return e.stopPropagation();
}